import { type FC, Suspense, useCallback, useState } from 'react';
import { useFragment, graphql } from 'react-relay';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid.tsx';
import ProjectCoverPhotoContainer from './ProjectCoverPhotoContainer.tsx';
import ProjectOverviewContainer from './ProjectOverviewContainer.tsx';
import ProjectViewMoreContainer from './ProjectViewMoreContainer.tsx';
import ProjectRelatedSearchesContainer from './ProjectRelatedSearchesContainer.tsx';
import ProjectPhotosGrid from './ProjectPhotosGrid.tsx';
import { PhotoLikesContextProvider } from './PhotoLikesContext.tsx';
import PhotoLikesLoader, { type PhotoLikesMap } from './PhotoLikesLoader.tsx';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';

import { useServerVars } from '../shared/ServerVarsContext.tsx';

import styles from './InteriorProjectContent.module.css';

import { type InteriorProjectContent_viewer$key } from './__generated__/InteriorProjectContent_viewer.graphql.ts';
import { type InteriorProjectContent_searchResult$key } from './__generated__/InteriorProjectContent_searchResult.graphql.ts';

type Props = {
    userId: string;
    openModal: (currentUrl: string) => void;
    searchResult: InteriorProjectContent_searchResult$key | null | undefined;
    viewer: InteriorProjectContent_viewer$key;
};

const InteriorProjectContent: FC<Props> = props => {
    const { openModal, viewer: viewerRef, searchResult: searchResultRef, userId } = props;

    const viewer = useFragment(
        graphql`
            fragment InteriorProjectContent_viewer on Viewer {
                ...ProjectRelatedSearchesContainer_viewer
            }
        `,
        viewerRef
    );

    const searchResult = useFragment(
        graphql`
            fragment InteriorProjectContent_searchResult on TradeInteriorProjectQueryOutput {
                ...PhotoLikesLoader_searchResult
                ...ProjectCoverPhotoContainer_searchResult
                ...ProjectOverviewContainer_searchResult
                ...ProjectPhotosGrid_searchResult
                ...ProjectViewMoreContainer_searchResult
            }
        `,
        searchResultRef
    );

    const isClient = useClientState();
    const { isMobile } = useServerVars();

    const [photoLikesMap, setPhotoLikesMap] = useState<PhotoLikesMap | null>(null);

    const onPhotoLikesLoaded = useCallback((photoLikes: PhotoLikesMap) => {
        setPhotoLikesMap(photoLikes);
    }, []);

    return (
        <PhotoLikesContextProvider photoLikesMap={photoLikesMap}>
            {isClient && (
                <Suspense fallback="">
                    <PhotoLikesLoader
                        searchResult={searchResult}
                        userId={userId}
                        onLoaded={onPhotoLikesLoaded}
                    />
                </Suspense>
            )}
            <div className={styles.content}>
                <div className={styles.topWrapper}>
                    <GridRow>
                        {!isMobile && (
                            <GridCol
                                tabletLandscape={6}
                                mobileOptions={{
                                    verticalAlign: 'stretch',
                                }}
                            >
                                <ProjectCoverPhotoContainer
                                    openModal={openModal}
                                    searchResult={searchResult}
                                />
                            </GridCol>
                        )}
                        <GridCol
                            tabletLandscape={6}
                            mobile={12}
                            mobileOptions={{
                                verticalAlign: 'stretch',
                            }}
                        >
                            <ProjectOverviewContainer
                                openModal={openModal}
                                searchResult={searchResult}
                            />
                        </GridCol>
                    </GridRow>
                </div>
                <ProjectPhotosGrid openModal={openModal} searchResult={searchResult} />
                <ProjectViewMoreContainer searchResult={searchResult} />
                <ProjectRelatedSearchesContainer viewer={viewer} />
            </div>
        </PhotoLikesContextProvider>
    );
};

export default InteriorProjectContent;
